<template>
  <!-- phones: one team at a time, two headline columns that sort, the rest of the player under a tap -->
  <div class="player-table">
    <div class="flex mb-2" role="tablist">
      <button
        v-for="t in teams"
        :key="t.side"
        type="button"
        role="tab"
        class="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-semibold border-b-[3px] min-w-0"
        :class="
          side === t.side
            ? [t.side === 'home_team' ? 'border-[#e59c98]' : 'border-[#bdbdfb]', 'text-gray-900']
            : 'border-transparent text-gray-500'
        "
        :aria-selected="side === t.side ? 'true' : 'false'"
        v-on:click="pick(t.side)"
      >
        <img :src="t.logo" class="w-4 h-auto flex-none" alt="" />
        <span class="truncate">{{ t.name }}</span>
      </button>
    </div>

    <!-- locked: one line here rather than a prompt on every row -->
    <router-link
      v-if="hasProps && locked && rows.length"
      to="/signup/"
      class="flex items-center gap-2 mb-2 rounded-lg bg-[#eef0ff] px-3 py-2 text-xs text-gray-700"
      v-on:click.native="upgradeClick"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" class="flex-none text-blue-700" aria-hidden="true">
        <path
          fill="currentColor"
          d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-7-2a2 2 0 0 1 4 0v2h-4z"
        />
      </svg>
      <span class="flex-1">Our chance for every player bet is for members</span>
      <span class="flex-none text-blue-700 font-bold uppercase underline">Unlock</span>
    </router-link>

    <p v-if="!rows.length" class="py-2 text-center text-gray-500 text-xs">
      {{ emptyText }}
    </p>

    <div v-else class="rounded-lg border border-gray-200 overflow-hidden">
      <div
        class="flex items-end px-2 py-2 text-[10px] font-semibold text-gray-700 select-none"
        :class="home ? 'bg-[#ffd5d5]' : 'bg-[#dcdcff]'"
      >
        <span class="flex-1 min-w-0 pb-2.5">Player</span>
        <template v-for="c in columns">
          <!-- the label sits centred over its numbers; the sort arrow (or the lock) goes under it -->
          <button
            :key="c.key"
            type="button"
            class="flex-none flex justify-center leading-tight whitespace-nowrap"
            :class="[c.width, c.sortable ? 'cursor-pointer' : 'cursor-default text-gray-400']"
            :title="c.title"
            :aria-sort="sort.key === c.key ? (sort.asc ? 'ascending' : 'descending') : null"
            v-on:click="c.sortable && sortBy(c.key)"
          >
            <span class="flex flex-col items-center" :class="{ 'text-gray-900': sort.key === c.key }">
              {{ c.label }}
              <span class="h-2.5 text-[8px] leading-[10px]">
                <template v-if="sort.key === c.key">{{ sort.asc ? "▲" : "▼" }}</template>
                <svg v-else-if="!c.sortable" width="8" height="8" viewBox="0 0 24 24" class="inline-block" aria-label="Members only">
                  <path
                    fill="currentColor"
                    d="M17 8h-1V6a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2zm-7-2a2 2 0 0 1 4 0v2h-4z"
                  />
                </svg>
              </span>
            </span>
          </button>
          <span v-if="c.market && hasProps" :key="c.key + '-add'" class="w-[22px] flex-none"></span>
        </template>
      </div>

      <div
        v-for="(item, i) in sorted.slice(0, visible)"
        :key="item.player_id || item.name"
        class="border-t border-gray-200"
        :class="i % 2 === 0 ? (home ? 'bg-[#FAEBEA]' : 'bg-[#F5F5FE]') : 'bg-white'"
      >
        <div class="flex items-center px-2 py-2.5 min-h-[52px]">
          <button
            type="button"
            class="flex-1 min-w-0 pr-1 text-left"
            :title="item.name"
            :aria-expanded="String(isOpen(item))"
            v-on:click="toggle(item)"
          >
            <!-- surname only so six numbers fit; the full name is the tooltip; the chevron rides on the end -->
            <span
              class="block text-[12px] font-semibold leading-tight break-words"
              :class="isOut(item.name) ? 'text-red-500' : 'text-gray-800'"
              >{{ firstNames(item.name) }}<span class="whitespace-nowrap">{{ lastName(item.name)
              }}<svg
                xmlns="http://www.w3.org/2000/svg"
                class="inline-block h-3 w-3 ml-0.5 -mt-px text-gray-400 transition-transform"
                :class="{ 'rotate-180': isOpen(item) }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2.5"
                aria-hidden="true"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg></span
            ></span>
            <span v-if="badge(item)" class="block text-[10px] font-semibold leading-tight mt-0.5" :class="badge(item).cls">{{
              badge(item).text
            }}</span>
          </button>
          <template v-for="c in columns">
            <PropHeadline
              v-if="c.market && hasProps"
              :key="c.key"
              class="flex-none"
              :playerProps="playerProps"
              :side="side"
              :item="item"
              :market="c.market"
              :line="c.line"
              :slipMatch="slipMatch"
            />
            <span
              v-else
              :key="c.key"
              class="flex-none text-center font-semibold tabular-nums"
              :class="[c.width, c.headline ? 'text-[14px]' : 'text-[13px]', statClass(item, c)]"
              >{{ c.show(item) }}</span
            >
          </template>
        </div>

        <!-- the player's other numbers, then their bets: our %, the best odds and whether it's a value pick -->
        <div v-if="isOpen(item)" class="px-2 pb-2.5">
          <div class="grid grid-cols-3 gap-px bg-gray-100 border border-gray-100 rounded-md overflow-hidden">
            <div v-for="d in details(item)" :key="d.label" class="bg-white flex flex-col items-center py-1.5 tabular-nums">
              <span class="text-[13px] leading-tight font-semibold" :class="toneClass(item, d.tone)">{{ d.value }}</span>
              <span class="text-[10px] text-gray-400">{{ d.label }}</span>
            </div>
          </div>
          <div v-if="hasProps" class="mt-2 rounded-md bg-white px-2 pt-1 pb-0.5 divide-y divide-gray-100">
            <div class="flex items-center pb-1 text-[10px] font-semibold text-gray-400 whitespace-nowrap">
              <span class="flex-1">Bet</span>
              <span class="w-[52px] flex-none text-center">Our %</span>
              <span class="w-[52px] flex-none text-center">Best odds</span>
              <span class="w-[70px] flex-none text-center">Value pick</span>
              <span class="w-[26px] flex-none"></span>
            </div>
            <PropRow
              v-for="m in markets"
              :key="m.market + m.line"
              :label="m.label"
              :playerProps="playerProps"
              :side="side"
              :item="item"
              :market="m.market"
              :line="m.line"
              :slipMatch="slipMatch"
            />
          </div>
        </div>
      </div>

      <div v-if="rows.length > 5" class="flex justify-center py-2 border-t border-gray-200" :class="home ? 'bg-[#ffd5d5]' : 'bg-[#dcdcff]'">
        <button type="button" class="text-gray-500 uppercase underline text-xxs" v-on:click="showMore">
          {{ moreLeft ? "Show more" : "Show less" }}
        </button>
      </div>
    </div>
    <p v-if="rows.length" class="mt-1 text-[10px] text-gray-400 text-right">
      {{ kind === "cards" ? "Mins and Fouls per game" : "Shots and SOT per game" }}
    </p>
  </div>
</template>

<script>
import PropHeadline from "./PropHeadline.vue";
import PropRow from "./PropRow.vue";
import { propsLocked } from "./propValue";
import { trackFeature } from "../../analytics";

const oneDp = (v) => Math.round((Number(v) || 0) * 10) / 10;
// per-game numbers keep their decimal (1.0, not 1) so the column lines up
const perGame = (v) => oneDp(v).toFixed(1);

export default {
  name: "PlayerTable",
  components: { PropHeadline, PropRow },
  props: {
    kind: { type: String, required: true }, // goals | cards
    match: { type: Object, required: true },
    homeList: { type: Array, required: true },
    awayList: { type: Array, required: true },
    // the parent table's sortValue(item, key, side): the number the column shows
    sortValue: { type: Function, required: true },
    homeInjured: { type: Array, default: () => [] },
    homeSuspended: { type: Array, default: () => [] },
    awayInjured: { type: Array, default: () => [] },
    awaySuspended: { type: Array, default: () => [] },
    playerProps: { type: Object, default: null },
    slipMatch: { type: Object, default: null },
    lineupConfirmed: { type: Boolean, default: false },
  },
  data() {
    return {
      side: "home_team",
      visible: 5,
      sort: { key: null, asc: false },
      open: {},
    };
  },
  computed: {
    teams() {
      return ["home_team", "away_team"].map((side) => ({
        side,
        name: this.match[side].name,
        logo: this.match[side].logo,
      }));
    },
    home() {
      return this.side === "home_team";
    },
    rows() {
      return this.home ? this.homeList : this.awayList;
    },
    injured() {
      return this.home ? this.homeInjured : this.awayInjured;
    },
    suspended() {
      return this.home ? this.homeSuspended : this.awaySuspended;
    },
    hasProps() {
      return !!(this.slipMatch && this.playerProps && (this.playerProps[this.side] || []).length);
    },
    locked() {
      return propsLocked(this.$store, this.slipMatch);
    },
    // the desktop table's numbers, our chance last; our chance doesn't sort while it's hidden behind "??"
    columns() {
      const stat = (key, label, title, width, show, tone) => ({ key, label, title, width, sortable: true, show, tone });
      const ours = (label, title, market, line) => ({
        key: `prop|${market}|${line}`,
        label,
        title,
        market,
        line,
        width: "w-[44px]",
        sortable: !this.locked,
        headline: true,
      });
      const apps = stat("appearances", "Apps", "Appearances", "w-[36px]", (item) => item.appearances || 0);
      if (this.kind === "cards") {
        return [
          apps,
          stat("avg_minutes", "Mins", "Average minutes per game", "w-[32px]", (item) => item.avg_minutes || 0),
          stat("yellow_cards_all_comps", "Yel", "Yellow cards", "w-[28px]", (item) => item.yellow_cards_all_comps || 0,
            (item) => ((item.yellow_cards_all_comps || 0) >= 2 ? "warn" : null)),
          stat("red_cards_all_comps", "Red", "Red cards", "w-[28px]", (item) => item.red_cards_all_comps || 0,
            (item) => ((item.red_cards_all_comps || 0) >= 1 ? "bad" : null)),
          stat("avg_fouls", "Fouls", "Average fouls per game", "w-[34px]", (item) => perGame(item.avg_fouls),
            (item) => (oneDp(item.avg_fouls) >= 1.5 ? "warn" : null)),
          this.hasProps
            ? ours("Booked", "Our chance of being booked", "card", "")
            : stat("card_prediction", "Booked", "Chance of being booked", "w-[66px]", (item) => item.card_prediction || "-"),
        ];
      }
      return [
        apps,
        stat("goals_all_comps", "Goals", "Goals this season", "w-[38px]", (item) => item.goals_all_comps || 0,
          (item) => (item.appearances && (item.goals_all_comps || 0) / item.appearances >= 0.4 ? "good" : null)),
        stat("avg_shots_total", "Shots", "Average shots per game", "w-[40px]", (item) => perGame(item.avg_shots_total)),
        stat("avg_shots_on_target", "SOT", "Average shots on target per game", "w-[36px]",
          (item) => perGame(item.avg_shots_on_target), (item) => (oneDp(item.avg_shots_on_target) >= 1 ? "good" : null)),
        this.hasProps
          ? ours("To score", "Our chance of scoring", "goal", "")
          : { ...stat("goal_prediction", "To score", "Chance of scoring", "w-[66px]", (item) => item.goal_prediction || "-"), headline: true },
      ];
    },
    markets() {
      return this.kind === "cards"
        ? [
            { label: "Booked", market: "card", line: "" },
            { label: "Fouls 1+", market: "fouls", line: "1+" },
            { label: "Tackles 1+", market: "tackles", line: "1+" },
          ]
        : [
            { label: "To score", market: "goal", line: "" },
            { label: "SOT 1+", market: "sot", line: "1+" },
            { label: "Shots 2+", market: "shots", line: "2+" },
          ];
    },
    // until a header is tapped: our chance, highest first (or the first column while it's locked)
    activeSort() {
      if (this.sort.key && this.columns.some((c) => c.key === this.sort.key && c.sortable)) return this.sort;
      const last = this.columns[this.columns.length - 1];
      const first = last.sortable ? last : this.columns[this.columns.length - 2];
      return { key: first.key, asc: false };
    },
    sorted() {
      const { key, asc } = this.activeSort;
      return [...this.rows].sort((a, b) => {
        const av = this.sortValue(a, key, this.side);
        const bv = this.sortValue(b, key, this.side);
        return asc ? av - bv : bv - av;
      });
    },
    moreLeft() {
      return this.visible < this.rows.length;
    },
    emptyText() {
      const where = this.home ? "home" : "away";
      return this.kind === "goals" ? `No recent ${where} team goal scorers` : `No recent ${where} team cards`;
    },
  },
  watch: {
    // the header arrow follows the sort that's in force
    activeSort: {
      immediate: true,
      handler(s) {
        if (this.sort.key !== s.key) this.sort = { ...s };
      },
    },
  },
  methods: {
    pick(side) {
      this.side = side;
      this.visible = 5;
    },
    sortBy(key) {
      this.sort = this.sort.key === key ? { key, asc: !this.sort.asc } : { key, asc: false };
    },
    upgradeClick() {
      trackFeature("betslip_upgrade_click", "betslip", { from: "player_table", kind: this.kind });
    },
    showMore() {
      this.visible = this.moreLeft ? Math.min(this.visible + 5, this.rows.length) : 5;
    },
    rowKey(item) {
      return `${this.side}|${item.player_id || item.name}`;
    },
    isOpen(item) {
      return !!this.open[this.rowKey(item)];
    },
    toggle(item) {
      this.$set(this.open, this.rowKey(item), !this.isOpen(item));
    },
    // surname only ("Haaland", "De Cuyper"); the last word keeps the chevron with it if it wraps
    firstNames(name) {
      const words = (name || "").split(" ");
      return words.length < 3 ? "" : words.slice(1, -1).join(" ") + " ";
    },
    lastName(name) {
      const i = (name || "").lastIndexOf(" ");
      return i < 0 ? name : name.slice(i + 1);
    },
    isOut(name) {
      return this.injured.includes(name) || this.suspended.includes(name);
    },
    propRow(item) {
      const rows = (this.playerProps && this.playerProps[this.side]) || [];
      return (
        (item.player_id && rows.find((r) => r.player_id === item.player_id)) ||
        rows.find((r) => r.name === item.name) ||
        null
      );
    },
    badge(item) {
      if (this.injured.includes(item.name)) return { text: "Injured", cls: "text-red-500" };
      if (this.suspended.includes(item.name)) return { text: "Suspended", cls: "text-red-500" };
      const row = this.propRow(item);
      if (row && row.status === "doubtful") {
        return { text: this.lineupConfirmed ? "Not in XI" : "Not in predicted XI", cls: "text-[#b45309]" };
      }
      return null;
    },
    // everything we have on the player that isn't a column, to read when the row is opened
    details(item) {
      const row = this.propRow(item);
      const expMins = row && row.minutes_exp ? `${Math.round(row.minutes_exp)}` : "–";
      const whole = (v) => (v === undefined || v === null ? "–" : Math.round(Number(v)));
      if (this.kind === "cards") {
        return [
          { label: "Fouls (total)", value: whole(item.fouls) },
          { label: "Mins (total)", value: whole(item.minutes_played) },
          { label: "Expected mins", value: expMins },
        ];
      }
      const xg = item.avg_xg === undefined || item.avg_xg === null ? null : Number(item.avg_xg);
      return [
        { label: "xG per game", value: xg === null ? "–" : xg.toFixed(2), tone: xg >= 0.35 ? "good" : null },
        { label: "Mins per game", value: whole(item.avg_minutes) },
        { label: "Expected mins", value: expMins },
        { label: "Assists", value: whole(item.assists_all_comps) },
        { label: "Shots (total)", value: whole(item.shots_total) },
        { label: "SOT (total)", value: whole(item.shots_on_target) },
      ];
    },
    statClass(item, c) {
      return this.toneClass(item, c.tone && c.tone(item));
    },
    toneClass(item, tone) {
      if (this.isOut(item.name)) return "text-red-500";
      return { good: "text-[#16a34a]", warn: "text-[#d97706]", bad: "text-red-600" }[tone] || "text-gray-800";
    },
  },
};
</script>

